import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './components/LanguageSwitcher';
import TechCarousel from './components/TechCarousel';

function CookieBanner() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <aside className="fixed bottom-6 left-4 right-4 z-[80] mx-auto max-w-xl rounded-lg border border-cyan-400/30 bg-zinc-950/95 p-5 text-white shadow-2xl shadow-black/40 backdrop-blur-md sm:left-6 sm:right-auto">
      <div className="flex items-start gap-4">
        <span className="material-symbols-outlined mt-0.5 text-cyan-400">cookie</span>
        <div className="min-w-0 flex-1">
          <h2 className="font-label-caps text-label-caps text-white">{t('landing.cookies.title')}</h2>
          <p className="mt-2 text-sm leading-6 text-zinc-300">{t('landing.cookies.description')}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              className="rounded bg-secondary-container px-5 py-2 text-sm font-semibold text-on-secondary-fixed transition-all duration-300 hover:brightness-110"
              type="button"
              onClick={() => setIsVisible(false)}
            >
              {t('landing.cookies.accept')}
            </button>
            <button
              className="rounded border border-white/15 px-5 py-2 text-sm font-semibold text-white transition-all duration-300 hover:bg-white/10"
              type="button"
              onClick={() => setIsVisible(false)}
            >
              {t('landing.cookies.close')}
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}

function Landing() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    alert(t('landing.contactThankYou'));
  };

  const handleLogoClick = () => {
    window.history.replaceState(null, '', window.location.pathname);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const serviceCards = [
    {
      icon: 'pentagon',
      title: t('landing.uiux.title'),
      desc: t('landing.uiux.description'),
      items: t('landing.uiux.items', { returnObjects: true }),
      center: false,
      color: 'text-violet-400',
    },
    {
      icon: 'terminal',
      title: t('landing.development.title'),
      desc: t('landing.development.description'),
      items: t('landing.development.items', { returnObjects: true }),
      center: false,
      color: 'text-sky-400',
    },
    {
      icon: 'shopping_bag',
      title: t('landing.ecommerce.title'),
      desc: t('landing.ecommerce.description'),
      items: t('landing.ecommerce.items', { returnObjects: true }),
      center: false,
      color: 'text-amber-400',
    },
    {
      icon: 'speed',
      title: t('landing.performance.title'),
      desc: t('landing.performance.description'),
      items: t('landing.performance.items', { returnObjects: true }),
      center: true,
      color: 'text-emerald-400',
    },
  ];

  return (
    <div className="min-h-screen bg-background text-on-background">
      <header className="fixed top-0 w-full z-50 bg-zinc-950/80 backdrop-blur-md border-b border-white/10 h-20">
        <div className="max-w-7xl mx-auto px-8 flex justify-between items-center h-full">
          <button type="button" onClick={handleLogoClick} className="flex items-center gap-3" aria-label="ElmakinaTech">
            <span className="material-symbols-outlined text-cyan-400">architecture</span>
            <span className="text-xl font-bold tracking-tighter text-white">ElmakinaTech</span>
          </button>
          <nav className="hidden md:flex items-center gap-10">
            <a className="text-cyan-400 font-medium font-label-caps text-label-caps hover:text-white transition-colors duration-200" href="#servicios">{t('landing.services')}</a>
            <Link className="text-zinc-400 font-label-caps text-label-caps hover:text-white transition-colors duration-200" to="/consultoria">{t('landing.consulting')}</Link>
            <a className="text-zinc-400 font-label-caps text-label-caps hover:text-white transition-colors duration-200" href="#contacto">{t('landing.contact')}</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-4">
            <LanguageSwitcher />
            <a href="#contacto" className="hidden sm:inline-flex bg-secondary-container text-on-secondary-fixed font-label-caps text-label-caps px-6 py-3 rounded hover:brightness-110 transition-all duration-300">
              {t('landing.getStarted')}
            </a>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="inline-flex md:hidden items-center justify-center w-10 h-10 text-white"
              aria-label="Menú"
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
                {menuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M3 12h18M3 6h18M3 18h18" />
                )}
              </svg>
            </button>
          </div>
          {menuOpen && (
            <div className="absolute top-full left-0 right-0 bg-zinc-950/95 backdrop-blur-md border-b border-white/10 md:hidden">
              <div className="flex flex-col px-8 py-6 gap-6">
                <a onClick={() => setMenuOpen(false)} className="text-cyan-400 font-label-caps text-label-caps hover:text-white transition-colors" href="#servicios">{t('landing.services')}</a>
                <Link onClick={() => setMenuOpen(false)} className="text-zinc-400 font-label-caps text-label-caps hover:text-white transition-colors" to="/consultoria">{t('landing.consulting')}</Link>
                <a onClick={() => setMenuOpen(false)} className="text-zinc-400 font-label-caps text-label-caps hover:text-white transition-colors" href="#contacto">{t('landing.contact')}</a>
                <a onClick={() => setMenuOpen(false)} href="#contacto" className="bg-secondary-container text-on-secondary-fixed font-label-caps text-label-caps px-6 py-3 rounded text-center hover:brightness-110 transition-all duration-300 sm:hidden">
                  {t('landing.getStarted')}
                </a>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="pt-20">
        <section id="inicio" className="relative min-h-[600px] md:min-h-[921px] flex items-center overflow-hidden -mt-12">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-cyan-900/10 opacity-50"></div>
            <img 
              className="w-full h-full object-cover opacity-45 mix-blend-screen saturate-150" 
              alt="A colorful web development workspace with code on a laptop"
              src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=2200&q=85" 
            />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-8 w-full -translate-y-8">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="hero-title text-4xl sm:text-5xl md:text-6xl lg:text-display-xl text-white mb-8">{t('landing.digitalExperiences')} <span className="text-cyan-400">{t('landing.convert')}</span>.</h1>
              <p className="font-body-lg text-body-lg text-on-surface-variant max-w-2xl mx-auto mb-12">
                {t('landing.description')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                <a href="#servicios" className="bg-secondary-container text-on-secondary-fixed font-label-caps text-label-caps px-10 py-5 rounded hover:bg-white transition-colors duration-300">
                  {t('landing.viewServices')}
                </a>
              </div>
            </div>
          </div>
          <div className="absolute bottom-12 right-8 hidden lg:block opacity-40">
             {/* <div className="flex flex-col gap-2 font-label-caps text-label-caps text-zinc-500 border-l border-zinc-800 pl-4">
              <span>LAT: 40.7128° N</span>
              <span>LON: 74.0060° W</span>
              <span>VER: 2024.04.1</span>
            </div> */}
          </div>
        </section>

        <section id="servicios" className="py-section-padding bg-surface-container-lowest overflow-hidden">
          <div className="max-w-7xl mx-auto px-8">
            <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
              <div className="max-w-xl">
                <h2 className="font-headline-lg text-headline-lg text-white mb-4">{t('landing.coreCapabilities')}</h2>
                <p className="font-body-md text-body-md text-on-surface-variant">{t('landing.capabilitiesDescription')}</p>
              </div>
            </div>
            <div className="relative overflow-x-hidden">
              <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#0b1326] to-transparent z-20" />
              <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#0b1326] to-transparent z-20" />
              <div className="flex animate-scroll hover:animate-paused gap-8 w-max">
                {[...serviceCards, ...serviceCards].map((card, i) => (
                  <div key={i} className={`glass-card p-6 sm:p-10 flex flex-col min-h-[350px] sm:min-h-[400px] min-w-[280px] w-[80vw] sm:min-w-[400px] sm:w-[400px] ${card.center ? 'text-center' : ''}`}>
                    <span className={`material-symbols-outlined ${card.color} mb-8 text-[40px] ${card.center ? 'mx-auto' : ''}`}>{card.icon}</span>
                    <h3 className={`font-headline-md text-headline-md text-white mb-4 ${card.center ? 'text-center' : ''}`}>{card.title}</h3>
                    <p className="font-body-md text-body-md text-on-surface-variant mb-8 flex-grow">{card.desc}</p>
                    <ul className="space-y-3 border-t border-white/5 pt-6">
                      {card.items.map((item, j) => (
                        <li key={j} className="font-label-caps text-[13px] text-zinc-500">• {item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="portfolio" className="py-section-padding bg-background">
          <div className="max-w-7xl mx-auto px-8">
            <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
              <div className="max-w-xl">
                <h2 className="font-headline-lg text-headline-lg text-white mb-4">{t('landing.selectedWork')}</h2>
                <p className="font-body-md text-body-md text-on-surface-variant">{t('landing.portfolioDescription')}</p>
              </div>
            </div>
            <div className="grid grid-cols-12 gap-8">
              <div className="col-span-12 lg:col-span-8 group relative overflow-hidden h-[300px] sm:h-[400px] lg:h-[600px] bg-zinc-900 border border-white/10">
                <img className="w-full h-full object-cover opacity-75 saturate-125 group-hover:scale-105 transition-transform duration-700" 
                  alt="Colorful financial analytics charts on a digital dashboard" 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1800&q=85" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80"></div>
                <div className="absolute bottom-0 left-0 p-6 sm:p-8 lg:p-12 w-full flex justify-between items-end">
                  <div>
                    <span className="font-label-caps text-label-caps text-cyan-400 mb-4 block">{t('landing.fintechEngine')}</span>
                    <h3 className="font-headline-lg text-headline-lg text-white mb-2">{t('landing.quantCapital')}</h3>
                    <p className="font-body-md text-zinc-400 max-w-md">{t('landing.quantCapitalDesc')}</p>
                  </div>
                  <span className="material-symbols-outlined text-white text-[48px] opacity-0 group-hover:opacity-100 transition-opacity duration-300">north_east</span>
                </div>
              </div>
              <div className="col-span-12 lg:col-span-4 group relative overflow-hidden h-[300px] sm:h-[400px] lg:h-[600px] bg-zinc-900 border border-white/10">
                <img className="w-full h-full object-cover opacity-75 saturate-125 group-hover:scale-105 transition-transform duration-700" 
                  alt="A colorful premium furniture ecommerce product scene" 
                  src="https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=1200&q=85" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80"></div>
                <div className="absolute bottom-0 left-0 p-6 sm:p-8 lg:p-12">
                  <span className="font-label-caps text-label-caps text-cyan-400 mb-4 block">{t('landing.ecommerceLuxury')}</span>
                  <h3 className="font-headline-md text-headline-md text-white mb-2">{t('landing.auraStudio')}</h3>
                  <p className="font-body-md text-zinc-400">{t('landing.auraStudioDesc')}</p>
                </div>
              </div>
              <div className="col-span-12 lg:col-span-4 group relative overflow-hidden h-[300px] sm:h-[400px] lg:h-[600px] bg-zinc-900 border border-white/10">
                <img className="w-full h-full object-cover opacity-75 saturate-125 group-hover:scale-105 transition-transform duration-700" 
                  alt="A SaaS analytics interface open on a laptop" 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=85" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80"></div>
                <div className="absolute bottom-0 left-0 p-6 sm:p-8 lg:p-12">
                  <span className="font-label-caps text-label-caps text-cyan-400 mb-4 block">{t('landing.saasPlatform')}</span>
                  <h3 className="font-headline-md text-headline-md text-white mb-2">{t('landing.locusAI')}</h3>
                  <p className="font-body-md text-zinc-400">{t('landing.locusAIDesc')}</p>
                </div>
              </div>
              <div className="col-span-12 lg:col-span-8 group relative overflow-hidden h-[300px] sm:h-[400px] lg:h-[600px] bg-zinc-900 border border-white/10">
                <img className="w-full h-full object-cover opacity-75 saturate-125 group-hover:scale-105 transition-transform duration-700" 
                  alt="A bright collaborative business team working on a digital project" 
                  src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1800&q=85" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80"></div>
                <div className="absolute bottom-0 left-0 p-6 sm:p-8 lg:p-12 w-full flex justify-between items-end">
                  <div>
                    <span className="font-label-caps text-label-caps text-cyan-400 mb-4 block">{t('landing.corporateRebrand')}</span>
                    <h3 className="font-headline-lg text-headline-lg text-white mb-2">{t('landing.vanguardGlobal')}</h3>
                    <p className="font-body-md text-zinc-400 max-w-md">{t('landing.vanguardGlobalDesc')}</p>
                  </div>
                  <span className="material-symbols-outlined text-white text-[48px] opacity-0 group-hover:opacity-100 transition-opacity duration-300">north_east</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-section-padding bg-surface-container-lowest border-y border-white/5">
          <div className="max-w-7xl mx-auto px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-16 items-center">
              <div className="lg:col-span-1">
                <h2 className="font-headline-lg text-headline-lg text-white mb-6">{t('landing.strategicPartnerships')}</h2>
                <p className="font-body-md text-body-md text-on-surface-variant mb-8">{t('landing.partnershipsDescription')}</p>
                <div className="flex gap-1">
                  <span className="material-symbols-outlined text-yellow-400 text-[28px]">star</span>
                  <span className="material-symbols-outlined text-yellow-400 text-[28px]">star</span>
                  <span className="material-symbols-outlined text-yellow-400 text-[28px]">star</span>
                  <span className="material-symbols-outlined text-yellow-400 text-[28px]">star</span>
                  <span className="material-symbols-outlined text-yellow-400 text-[28px]">star</span>
                </div>
              </div>
              <div className="lg:col-span-2 grid md:grid-cols-2 gap-8">
                <div className="glass-card p-6 md:p-8 lg:p-12">
                  <span className="material-symbols-outlined text-cyan-400 mb-6">format_quote</span>
                  <p className="font-body-lg text-white italic mb-10">"{t('landing.testimonial1Quote')}"</p>
                    <div className="flex items-center gap-4">
                    <img className="w-12 h-12 rounded object-cover" src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=100&q=80" alt={t('landing.marcusReed')} />
                    <div>
                      <h4 className="font-label-caps text-[12px] text-white">{t('landing.marcusReed')}</h4>
                      <p className="font-label-caps text-[10px] text-zinc-500">{t('landing.ctoNexusFlow')}</p>
                    </div>
                  </div>
                </div>
                <div className="glass-card p-6 md:p-8 lg:p-12">
                  <span className="material-symbols-outlined text-cyan-400 mb-6">format_quote</span>
                  <p className="font-body-lg text-white italic mb-10">"{t('landing.testimonial2Quote')}"</p>
                  <div className="flex items-center gap-4">
                    <img className="w-12 h-12 rounded object-cover" src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=100&q=80" alt={t('landing.sarahChen')} />
                    <div>
                      <h4 className="font-label-caps text-[12px] text-white">{t('landing.sarahChen')}</h4>
                      <p className="font-label-caps text-[10px] text-zinc-500">{t('landing.headOfProductVelocity')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="contacto" className="py-section-padding bg-background relative overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-[0.02] select-none">
            <span className="text-[30vw] font-black tracking-tighter">CONTACT</span>
          </div>
          <div className="max-w-7xl mx-auto px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
              <div>
                <span className="font-label-caps text-label-caps text-cyan-400 mb-6 block">{t('landing.readyToStart')}</span>
                <h2 className="font-headline-lg text-headline-lg text-white mb-8">{t('landing.letsTalk')}</h2>
                <p className="font-body-lg text-on-surface-variant mb-12 max-w-md">{t('landing.contactDescription')}</p>
                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <span className="material-symbols-outlined text-cyan-400">mail</span>
                    <div>
                      <p className="font-label-caps text-zinc-500 text-[10px] mb-1">{t('landing.inquiries')}</p>
                      <p className="text-white font-medium">{t('landing.email')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="material-symbols-outlined text-cyan-400">location_on</span>
                    <div>
                      <p className="font-label-caps text-zinc-500 text-[10px] mb-1">{t('landing.hq')}</p>
                      <p className="text-white font-medium">{t('landing.address')}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="glass-card p-6 sm:p-8 lg:p-12">
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="font-label-caps text-[10px] text-zinc-400">{t('landing.form.name')}</label>
                      <input 
                        className="w-full bg-zinc-950 border border-white/10 p-4 text-white focus:border-cyan-400 focus:ring-0 transition-colors" 
                        type="text"
                        placeholder={t('landing.form.namePlaceholder')}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="font-label-caps text-[10px] text-zinc-400">{t('landing.form.email')}</label>
                      <input 
                        className="w-full bg-zinc-950 border border-white/10 p-4 text-white focus:border-cyan-400 focus:ring-0 transition-colors" 
                        type="email"
                        placeholder={t('landing.form.emailPlaceholder')}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="font-label-caps text-[10px] text-zinc-400">{t('landing.form.service')}</label>
                    <select className="w-full bg-zinc-950 border border-white/10 p-4 text-white focus:border-cyan-400 focus:ring-0 transition-colors">
                      <option>{t('landing.form.uiuxDesign')}</option>
                      <option>{t('landing.form.fullStack')}</option>
                      <option>{t('landing.form.ecommerce')}</option>
                      <option>{t('landing.form.other')}</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="font-label-caps text-[10px] text-zinc-400">{t('landing.form.message')}</label>
                    <textarea 
                      className="w-full bg-zinc-950 border border-white/10 p-4 text-white focus:border-cyan-400 focus:ring-0 transition-colors" 
                      rows="4"
                      placeholder={t('landing.form.messagePlaceholder')}
                    ></textarea>
                  </div>
                  <button className="w-full bg-secondary-container text-on-secondary-fixed font-label-caps text-label-caps py-5 rounded hover:brightness-110 transition-all duration-300" type="submit">
                    {t('landing.form.sendMessage')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <TechCarousel />

      <footer className="bg-zinc-950 w-full py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-8 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-3">
            <span className="text-lg font-black text-white tracking-tighter">ElmakinaTech</span>
          </div>
          <div className="flex gap-6 md:gap-12">
            <a className="text-zinc-500 font-label-caps text-sm uppercase tracking-widest hover:text-cyan-400 transition-all duration-300" href="#servicios">{t('landing.footer.services')}</a>
            <Link className="text-zinc-500 font-label-caps text-sm uppercase tracking-widest hover:text-cyan-400 transition-all duration-300" to="/consultoria">{t('landing.footer.consulting')}</Link>
            <a className="text-zinc-500 font-label-caps text-sm uppercase tracking-widest hover:text-cyan-400 transition-all duration-300" href="#contacto">{t('landing.footer.contact')}</a>
          </div>
          <p className="text-zinc-500 font-label-caps text-xs uppercase tracking-widest">
            {t('landing.footer.copyright')}
          </p>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
}

export default Landing;
